import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { analyticsAPI, builderAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Users, Briefcase, Building2, FileText, Globe, MapPin, 
  TrendingUp, AlertCircle, Plus, ArrowUpRight 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BuilderDashboard = () => {
  const { user, builder, hasRole } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const canCreateProject = hasRole(['super_admin', 'builder_admin']);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await analyticsAPI.getDashboard();
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: 'Total Leads', 
      value: stats?.total_leads || 0, 
      icon: Briefcase, 
      color: 'bg-blue-500',
      href: '/builder/leads'
    },
    { 
      title: 'New Leads', 
      value: stats?.new_leads || 0, 
      icon: TrendingUp, 
      color: 'bg-emerald-500',
      href: '/builder/leads?status=new'
    },
    { 
      title: 'Total Partners', 
      value: stats?.total_partners || 0, 
      icon: Users, 
      color: 'bg-amber-500',
      href: '/builder/partners'
    },
    { 
      title: 'Pending Approvals', 
      value: stats?.pending_approvals || 0, 
      icon: AlertCircle, 
      color: 'bg-orange-500',
      href: '/builder/partners?status=pending'
    },
    { 
      title: 'Pending Taggings', 
      value: stats?.pending_taggings || 0, 
      icon: FileText, 
      color: 'bg-violet-500',
      href: '/builder/tagging?status=pending'
    },
    { 
      title: 'Site Visits Today', 
      value: stats?.site_visits_today || 0, 
      icon: MapPin, 
      color: 'bg-rose-500',
      href: '/builder/site-visits'
    },
    { 
      title: 'Total Projects', 
      value: stats?.total_projects || 0, 
      icon: Building2, 
      color: 'bg-slate-600',
      href: '/builder/projects'
    },
    { 
      title: 'Microsites Pending', 
      value: stats?.microsites_pending || 0, 
      icon: Globe, 
      color: 'bg-cyan-500',
      href: '/builder/microsites?status=pending'
    },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="builder-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Welcome back, {user?.name}</h1>
          <p className="text-muted-foreground mt-1">
            {builder?.name || 'Builder'} Dashboard
          </p>
        </div>
        <div className="flex gap-2">
          {canCreateProject && (
            <Button onClick={() => navigate('/builder/projects')} data-testid="btn-add-project">
              <Plus className="h-4 w-4 mr-2" />
              Add Project
            </Button>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Card 
            key={stat.title} 
            className="summary-card cursor-pointer animate-slide-up"
            style={{ animationDelay: `${index * 0.05}s` }}
            onClick={() => navigate(stat.href)}
            data-testid={`stat-${stat.title.toLowerCase().replace(/\s/g, '-')}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className={`p-2 rounded-lg ${stat.color}`}>
                  <stat.icon className="h-5 w-5 text-white" />
                </div>
                <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="mt-4">
                <p className="text-2xl md:text-3xl font-bold">{stat.value}</p>
                <p className="text-sm text-muted-foreground mt-1">{stat.title}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button 
              variant="outline" 
              className="w-full justify-start h-12"
              onClick={() => navigate('/builder/partners?status=pending')}
            >
              <Users className="h-4 w-4 mr-3" />
              Review Partner Applications
              {stats?.pending_approvals > 0 && (
                <span className="ml-auto bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs">
                  {stats.pending_approvals}
                </span>
              )}
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start h-12"
              onClick={() => navigate('/builder/microsites?status=pending_approval')}
            >
              <Globe className="h-4 w-4 mr-3" />
              Approve Microsites
              {stats?.microsites_pending > 0 && (
                <span className="ml-auto bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded-full text-xs">
                  {stats.microsites_pending}
                </span>
              )}
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start h-12"
              onClick={() => navigate('/builder/analytics')}
            >
              <TrendingUp className="h-4 w-4 mr-3" />
              View Partner Performance
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Builder Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div 
                className="h-16 w-16 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: builder?.branding?.primary_color || '#0F172A' }}
              >
                <Building2 className="h-8 w-8 text-white" />
              </div>
              <div>
                <p className="font-semibold">{builder?.name || 'Your Builder'}</p>
                <p className="text-sm text-muted-foreground">{builder?.contact_email}</p>
              </div>
            </div>
            <div className="pt-4 border-t">
              <p className="text-sm text-muted-foreground mb-2">Enabled Modules</p>
              <div className="flex flex-wrap gap-1">
                {builder?.enabled_modules?.slice(0, 6).map((module) => (
                  <span 
                    key={module} 
                    className="px-2 py-1 bg-muted rounded text-xs capitalize"
                  >
                    {module.replace(/_/g, ' ')}
                  </span>
                ))}
                {builder?.enabled_modules?.length > 6 && (
                  <span className="px-2 py-1 bg-muted rounded text-xs">
                    +{builder.enabled_modules.length - 6} more
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BuilderDashboard;
